import * as React from 'react';
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range';

import { i18n } from '@ciao/shared';
import { SelectTimeSlot } from './SelectTimeSlot';
import { SelectTimeSlotProps, SelectTimeSlotState, TimeSlotsType } from './model';

export type { SelectTimeSlotProps };

const MIN_TIME = '07:00';
const MAX_TIME = '19:00';
const MINUTE_INTERVAL = 15;

/**
 * SelectTimeSlot is an UI element to allow the user to make a time slot selection, by having two actionable items that
 *  allow to select a 'from' and 'to' time through native time pickers (using react-native-modal-datetime-picker).
 *
 * Example usecases:
 * - Used on the timeslot-enabled 'create reservation' screen to select a time slot.
 *
 * @example
 * <WBSelectTimeSlot label='Timeslot: ' placeholder='From time' placeholder2='To time' />
 *
 * @property {bool} isDisabled determines disabled state for the component. Disables any user interaction when true.
 * @property {function} onChange a callback called when the user changes a value. The callback is passed an array containing both values in form ['hh:mm', 'hh:mm']
 * @property {string} icon the icon to be displayed at the far left of the component.
 * @property {string} label the label to be displayed within the component.
 * @property {string} placeholder the placeholder to be displayed on the far right of the component for the 'from' value, displayed when no value is passed.
 * @property {string} placeholder2 the placeholder to be displayed on the far right of the component for the 'to' value, displayed when no value is passed.
 * @property {array} value the value to pre-populate the component with in the form of ['hh:mm', 'hh:mm']
 * @property {string} valuePrefix a prefix for the displayed 'from' value, e.g. 'From: '
 * @property {string} value2Prefix a prefix for the displayed 'from' value, e.g. 'To: '
 */

export class WBSelectTimeSlot extends React.Component<SelectTimeSlotProps, SelectTimeSlotState> {
	static defaultProps: SelectTimeSlotProps;

	constructor(props: SelectTimeSlotProps) {
		super(props);

		this.state = {
			// TimeSlots hard-coded data.
			// In the future this data should come from the API, that would change depending on the customer.
			timeSlots: [
				{
					label: i18n.t('screens.reservation.timeslotSelection.list.fullDay'),
					placeholder: '08:00 - 18:00',
					value: ['08:00', '18:00'],
				},
				{
					label: i18n.t('screens.reservation.timeslotSelection.list.morning'),
					placeholder: '08:00 - 13:00',
					value: ['08:00', '13:00'],
				},
				{
					label: i18n.t('screens.reservation.timeslotSelection.list.afternoon'),
					placeholder: '13:00 - 18:00',
					value: ['13:00', '18:00'],
				},
				{
					label: i18n.t('screens.reservation.timeslotSelection.list.other'),
					customSelection: true,
				},
			],
			startVisible: false,
			endVisible: false,
			timeSlot: -1,
			value: props.value,
		};

		this.onCancel = this.onCancel.bind(this);
		this.onConfirm = this.onConfirm.bind(this);
		this.onTimeSlotSelect = this.onTimeSlotSelect.bind(this);
		this.toggleTimeSelect = this.toggleTimeSelect.bind(this);
	}

	componentDidMount() {
		const { timeSlots, value } = this.state;
		if (!value || !value[0] || !value[1]) {
			return;
		}

		let timeSlot = timeSlots.findIndex(
			(item) => item.value && item.value[0] === value[0] && item.value[1] === value[1],
		);
		if (timeSlot < 0) {
			timeSlot = timeSlots.findIndex((item) => item.customSelection);
		}

		this.setState({ timeSlot });
	}

	/**
	 * Picks up the new value, makes sure it behaves according to the contraints (min and max time, not overlapping, etc)
	 *  and sets the new state accordingly
	 *
	 * @param {array} value containing start and end time in the form of [xx:xx, xx:xx]
	 */
	setValue(value: string[]) {
		const { onChange } = this.props;

		value[0] = this.constrainTime(value[0]);
		value[1] = this.constrainTime(value[1]);

		value = this.spaceValue(value);

		this.setState({ value }, onChange && onChange.bind(null, value));
	}

	onCancel(select: string) {
		this.toggleTimeSelect(select, false);
	}

	onConfirm(select: string, newValue: string) {
		const { value } = this.state;

		this.toggleTimeSelect(select, false);

		switch (select) {
			case 'start':
				value[0] = newValue;
				break;

			case 'end':
				value[1] = newValue;
				break;
		}

		this.setValue(value);
	}

	/**
	 * Saves timeSlot selection on state
	 *
	 * @param {number} timeSlot Index of selected timeSlot
	 */
	onTimeSlotSelect(index: number) {
		const { timeSlots } = this.state;
		const timeSlot = timeSlots[index];

		this.setState({ timeSlot: index });

		if (!timeSlot) {
			return;
		}

		if (timeSlot.customSelection) {
			this.setValue([MIN_TIME, MAX_TIME]);

			return;
		}

		this.setValue(timeSlot.value);
	}

	/**
	 * Converts the given <date> object into a (format xx.xx) string value
	 *
	 * @param {date} date Date object
	 */
	dateToTimeString(date: number) {
		return moment(date).format('HH:mm');
	}

	/**
	 * Converts the given <time> value into a date object set to the current date with the given <time>
	 *
	 * @param {string} time String in xx:xx format
	 */
	timeStringToDate(time: string) {
		const valueArray = time.split(':');

		const date = new Date();
		date.setHours(parseInt(valueArray[0]), parseInt(valueArray[1]), 0);

		return date;
	}

	/**
	 * Constrains the given time and makes sure it's between the min and max values as defined.
	 * @param {string} time String in xx:xx format
	 */
	constrainTime(time: string) {
		if (!time) {
			return time;
		}

		let [hours, minutes] = time.split(':').map((n) => parseInt(n));
		let [minHours, minMinutes] = MIN_TIME.split(':').map((n) => parseInt(n));
		let [maxHours, maxMinutes] = MAX_TIME.split(':').map((n) => parseInt(n));

		if (hours < minHours || (hours === minHours && minutes < minMinutes)) {
			hours = minHours;
			minutes = minMinutes;
		} else if (hours > maxHours || (hours === maxHours && minutes > maxMinutes)) {
			hours = maxHours;
			minutes = maxMinutes;
		}

		return `${`${hours}`.padStart(2, '0')}:${`${minutes}`.padStart(2, '0')}`;
	}

	/**
	 * Takes the given <value> in format [xx:xx, xx:xx] and makes sure there is a minimum of 15 minutes in between these values
	 * @param {array} value The component's value
	 */
	spaceValue(value: string[]) {
		let [start, end] = value;

		if (!start || !end) {
			return value;
		}

		let startDateEpoch = this.timeStringToDate(start).getTime() / 1000;
		let endDateEpoch = this.timeStringToDate(end).getTime() / 1000;

		const diff = endDateEpoch - startDateEpoch;
		const diffMinutes = diff / 60;

		if (diffMinutes >= 15) {
			return value;
		}

		// Apparently the enddate is behind the startdate. Make sure that's not the case.
		if (diffMinutes < 0) {
			endDateEpoch = startDateEpoch;
			end = this.dateToTimeString(new Date().setTime(endDateEpoch * 1000));
		}

		if (end === MAX_TIME) {
			// set start date to end date - 15 minutes, as end date is already at the MAX_VALUE value
			start = this.dateToTimeString(new Date().setTime((endDateEpoch - MINUTE_INTERVAL * 60) * 1000));
		} else {
			// set end date to start date + 15 minutes
			end = this.dateToTimeString(new Date().setTime((startDateEpoch + MINUTE_INTERVAL * 60) * 1000));
		}

		return [start, end];
	}

	toggleTimeSelect(select: string, toggle?: boolean) {
		if (typeof toggle === 'undefined') {
			toggle = this.state[`${select}Visible`];
		}

		this.setState((prevState) => ({ ...prevState, [`${select}Visible`]: toggle }));
	}

	render() {
		const { endVisible, startVisible, timeSlot, timeSlots, value } = this.state;
		const { icon, isDisabled, label, placeholder, placeholder2, valuePrefix, value2Prefix } = this.props;

		const startTimeValue = value[0];
		const endTimeValue = value[1];

		return (
			<SelectTimeSlot
				endTimeValue={endTimeValue}
				endVisible={endVisible}
				icon={icon}
				isDisabled={isDisabled}
				label={label}
				minuteInterval={MINUTE_INTERVAL}
				onCancel={this.onCancel}
				onConfirm={this.onConfirm}
				onTimeSlotSelect={this.onTimeSlotSelect}
				placeholder={placeholder}
				placeholder2={placeholder2}
				startTimeValue={startTimeValue}
				startVisible={startVisible}
				timeSlot={timeSlot}
				timeSlots={timeSlots as TimeSlotsType[]}
				toggleTimeSelect={this.toggleTimeSelect}
				value={value}
				value2Prefix={value2Prefix}
				valuePrefix={valuePrefix}
			/>
		);
	}
}

WBSelectTimeSlot.defaultProps = {
	icon: undefined,
	isDisabled: false,
	label: '',
	placeholder: '',
	placeholder2: '',
	value: ['', ''],
	valuePrefix: '',
	value2Prefix: '',
};
